import React from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import useThemeMode from '../../hooks/useThemeMode'

type ActionVariant = 'edit' | 'delete' | 'restore' | 'danger'

interface ActionIconButtonProps {
  tooltip: string
  icon: React.ReactNode
  onClick: () => void
  variant?: ActionVariant
  bordered?: boolean
  ariaLabel?: string
  /**
   * When true the button is inert but the tooltip still explains WHY — use it
   * for refused actions (e.g. deleting the last active Super Admin) so the
   * reason is discoverable instead of the control silently doing nothing.
   */
  disabled?: boolean
}

const ActionIconButton = ({
  tooltip,
  icon,
  onClick,
  variant = 'edit',
  bordered = false,
  ariaLabel,
  disabled = false,
}: ActionIconButtonProps) => {
  const { theme, isDark } = useThemeMode()

  const variantStyles: Record<ActionVariant, object> = {
    edit: {
      color: theme.palette.primary.main,
      '&:hover': {
        background: theme.colors.primary.lighter,
        ...(bordered && { borderColor: theme.palette.primary.main }),
      },
      ...(bordered && { border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}` }),
    },
    delete: {
      color: theme.palette.error.main,
      '&:hover': {
        background: theme.colors.error.lighter,
        ...(bordered && { borderColor: theme.palette.error.main }),
      },
      ...(bordered && { border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}` }),
    },
    restore: {
      color: theme.palette.primary.main,
      '&:hover': { background: theme.colors.primary.lighter },
    },
    danger: {
      color: theme.palette.error.main,
      '&:hover': { background: theme.colors.error.lighter },
    },
  }

  const button = (
    <IconButton
      sx={variantStyles[variant]}
      color="inherit"
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || tooltip}
    >
      {icon}
    </IconButton>
  )

  return (
    <Tooltip title={tooltip} arrow>
      {/* A disabled MUI button fires no pointer events, so the tooltip needs a
          live wrapper element to hang off — otherwise the reason never shows. */}
      {disabled ? (
        <Box component="span" sx={{ display: 'inline-flex' }}>
          {button}
        </Box>
      ) : (
        button
      )}
    </Tooltip>
  )
}

export default ActionIconButton
